import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-fd-sm font-geist text-[13px] font-medium',
    'transition-[background,color,border,box-shadow] duration-fd-fast ease-fd-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-accent focus-visible:ring-offset-2 focus-visible:ring-offset-fd-bg',
    'disabled:opacity-50 disabled:pointer-events-none',
    '[&_svg]:size-3.5 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        // Canonical race-yellow CTA: the accent does not theme and its ink is
        // ALWAYS dark (CLAUDE.md) — text-fd-bg went white-on-yellow on the
        // light-canonical surfaces.
        primary:
          'bg-fd-accent text-[oklch(0.16_0.005_95)] font-semibold hover:bg-[oklch(0.84_0.15_75)]',
        default:
          'bg-fd-surface-1 text-fd-text border border-fd-border hover:bg-fd-surface-2',
        outline:
          'bg-transparent text-fd-text border border-fd-border hover:bg-fd-surface-1',
        ghost:
          'bg-transparent text-fd-text-muted hover:bg-fd-surface-1 hover:text-fd-text',
        link: 'bg-transparent text-fd-accent underline-offset-4 hover:underline px-0 h-auto',
        destructive:
          'bg-fd-bad/15 text-fd-bad border border-fd-bad/30 hover:bg-fd-bad/25',
      },
      size: {
        sm: 'h-7 px-2.5 text-[12px]',
        default: 'h-8 px-3.5',
        lg: 'h-10 px-5 text-[14px]',
        icon: 'h-8 w-8 p-0',
        'icon-sm': 'h-7 w-7 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
